import turfArea from '@turf/area';
import bBoxPolygon from '@turf/bbox-polygon';
import dayjs from 'dayjs';
import { cellToBoundary } from 'h3-js';
import { round } from 'mathjs';
import { intersection, isEmpty, isNil, mapObjIndexed, or } from 'ramda';

import { countries } from '../../../helpers/country';

export const SCORE_COLOR = 'var(--color-chart-accent, #677632';
export const SCORE_COLOR_MUTED = 'var(--color-chart-accent-muted, #cad69e)';
export const VALUE_TEXT_COLOR_LIGHT =
  'var(--color-chart-text-value-light, #30312b)';
export const VALUE_TEXT_COLOR_DARK =
  'var(--color-chart-text-value-dark, #ffffff)';

export const formatDate = (date) =>
  date === 'null' ? undefined : dayjs(date).format('DD.MM.YYYY');

export const setLayerVisibility = (map, layerName, isVisible) => {
  if (map && map.getLayer(layerName)) {
    map.setLayoutProperty(
      layerName,
      'visibility',
      isVisible ? 'visible' : 'none',
    );
  }
};

export const isMapboxNil = (value) =>
  or(or(isNil(value), value === 'null'), isEmpty(value));

export const getValueOrDefault = ({ value, unit = '', defaultValue = '' }) => {
  if (isMapboxNil(value)) {
    return defaultValue;
  }
  return `${value} ${unit}`.trim();
};

export const simplifyBoundingBoxCoordinates = (
  boundingBox,
  fractionDigits = 8,
) => [
  [
    round(boundingBox[0][0], fractionDigits),
    round(boundingBox[0][1], fractionDigits),
  ],
  [
    round(boundingBox[1][0], fractionDigits),
    round(boundingBox[1][1], fractionDigits),
  ],
];

const isJSON = (str) => {
  try {
    const jsonObject = JSON.parse(str);
    return Array.isArray(jsonObject) || typeof jsonObject === 'object';
  } catch (_e) {
    return false;
  }
};

const parseMapboxFeature = (value) => {
  if (typeof value === 'string' && value === 'null') {
    return null;
  }
  if (typeof value === 'string' && isJSON(value)) {
    return JSON.parse(value);
  }
  return value;
};

export const getFeatureFromMapboxLayer = ({ e, map, layer }) => {
  const mapboxFeature = map
    ?.queryRenderedFeatures(e?.point)
    .find((feature) => feature?.layer?.id === layer);

  if (!mapboxFeature) {
    return null;
  }

  return {
    geometry: mapboxFeature.geometry,
    properties: mapObjIndexed(parseMapboxFeature, mapboxFeature?.properties),
  };
};

export const getDefaultCurrencyCodeByCountry = (country) => {
  switch (country) {
    case countries.FINLAND:
      return 'EUR';
    case countries.DENMARK:
      return 'DKK';
    case countries.SWEDEN:
      return 'SEK';
    case countries.NORWAY:
      return 'NOK';
    default:
      return null;
  }
};

export const intersects = (arrayA, arrayB) =>
  !isEmpty(intersection(arrayA?.map(String) || [], arrayB?.map(String) || []));

export const convertH3ToPolygon = (hexagonCellId) => ({
  coordinates: [cellToBoundary(hexagonCellId, true)],
  type: 'Polygon',
});

export const getMapBoundsArea = (mapBounds) => {
  if (!mapBounds) return null;
  const bBox = mapBounds?.flat();

  const polygon = bBoxPolygon(bBox);
  return turfArea(polygon);
};

export const toFeatureCollection = (features) => ({
  type: 'FeatureCollection',
  features,
});
